import Cookies from 'js-cookie'
import Vue from 'vue'
import axios from 'axios'
import { getTextContentFromHtml, isProd, isVendorXiBao, isWeixin, setupShence, setupWXShare } from '@/helpers'
import { INPUT_TYPES } from '@/pages/Survey/utils'
import { getHideParams } from '@/hide'

const state = Vue.observable({
  version: '0.0.1',
  httpErr: null,
  tmpChatResult: {},
  tmpSurveyBaseInfo: {},
  // 根据URL隐藏相关按钮
  hide: getHideParams() || {
    again: false, // 再次测评按钮
    askDoctor: false, // 问医生
    recommend: false // 推荐模块
  }
})

const gwBaseURL = process.env.APP_TICKET_ENDPOINT.substring(0,
  process.env.APP_TICKET_ENDPOINT.indexOf('/v1/users/webUserInfo')
)
const baseURL = process.env.API_ENDPOINT
const isMock = false
const apiOptions = {
  withCredentials: true,
  baseURL
}

if (isMock) {
  apiOptions.adapter = import('@/mocks').then(({ mockAxiosAdapter }) => {
    return mockAxiosAdapter({
      baseURL
    })
  })
}

const httpClient = axios.create(apiOptions)
const httpGWClient = axios.create({
  baseURL: gwBaseURL
})

if (!isMock) {
  const MAPPINGS = {
    getChatQuestions0: '/assess/v1/baseoptions/query',
    getChatQuestions: '/assess/v1/assessments/query',
    getMineSurveyList: '/assessuser/v1/answer/query',
    saveChatQuestions: '/assess/v1/answer'
  }

  httpClient.interceptors.request.use(config => {
    let url = config.url
    if (MAPPINGS.hasOwnProperty(url)) {
      config.url = [config.baseURL, MAPPINGS[url]].join('')
    }
    return config
  })
}

const resolveLocalToken = () => Cookies.get('yly_auth') || Cookies.get('yly_token')
const withDebugToken = !isProd && !isWeixin()
const DEBUG_TOKEN = () => resolveLocalToken() ||
  'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJ3eEFjY291bnRJZCI6MTM0MjM2NTA3NTQ3ODA0MDU3OCwiY291bnRyeSI6IuS4reWbvSIsInByaXZpbGVnZXMiOltdLCJ1bmlvbmlkIjoib2psNlgxQS01OHJyMFBTRzVNTWlSWHJxTWZYQSIsImNpdHkiOiIiLCJvcGVuaWQiOiJvNkVtdXc3dTZkS2hoYks2S2Y0cWZSWjJNaEpBIiwic2V4IjoxLCJsYW5ndWFnZSI6InpoX0NOIiwiZnJvbUlkIjoxMDAyLCJhY2NvdW50SWQiOjEyNDAxMTgyNjkzNTU3MTY2MDksInByb3ZpbmNlIjoiIiwicGhvbmUiOiIxMzA1MjMxMzQ0MyIsImFwcGlkIjoid3hmYWI0MWRjZTM2M2NmNjgyIiwibmlja25hbWUiOiJjbGllbiIsImhlYWRpbWd1cmwiOiJodHRwczovL3RoaXJkd3gucWxvZ28uY24vbW1vcGVuL3ZpXzMyL2ljb1FzN3BjcnpOM0s1SnREZUp0djZ6VFpBT2VtMGRVSjFkTThzc1JSMUtvYU5BVGdpYmtFNHFJMXIyZ3o0Rk1VY2liNXRGbXNGeGJSNDBIWmliZTdMTUxXZy8xMzIiLCJuYW1lIjoiY2xpZW4iLCJleHAiOjE2MTc1OTQxMTcsImlhdCI6MTYwODk1NDExN30.h41GnRnZT466yvErQYjQvymqRjaZ6cfcGq6lFXSplaDjyYwrGkOYmr0-T78sOt9yS8fx6xnNDP273wvSqH94Jg'

httpClient.interceptors.request.use(config => {
  const token = withDebugToken ? DEBUG_TOKEN.call(null) : resolveLocalToken()

  if (token) {
    config.headers = {
      ...config.headers,
      token
    }
  }
  return config
})

/**
 * @param e
 */
function handleHttpError (e) {
  let errorMsg = '数据获取失败了...'

  if (!isProd) {
    errorMsg = `[debug] ${(e && e.isAxiosError) ? `[${e.response.statusText}]` : ''}`
    errorMsg += `${e.message} .`
  }

  state.httpErr = errorMsg

  throw e
}

const actions = {
  /**
   * @param code
   * @return {Promise<undefined>}
   */
  async getSurveyBaseInfo (code, interest = '') {
    try {
      const { data } = await httpClient.get(`${interest}/assess/v1/assessment/${code}`)
      return data.data.cpLists[0]
    } catch (e) {
      handleHttpError(e)
    }
  },

  /**
   * 获取问答列表数据
   * @param query
   */
  async getChatQuestions (query) {
    try {
      const r0 = await httpClient.post('getChatQuestions0', query)
      let { baseQuestion } = r0.data.data

      if (!baseQuestion) {
        baseQuestion = []
      }

      const { data } = await httpClient.post('getChatQuestions', query)

      return baseQuestion.concat(data.data.assessQuestion.map((q, i) => {
        if (q.suggest) {
          q.suggest = getTextContentFromHtml(q.suggestHtml)
        }

        // TODO: DEBUG
        // if (!i) {
        //   q.subType = 5 // 多选
        // }
        return q
      }) || []).map(fixBackendToInputType)

      /**
       * @param meta
       * @returns {{showType}|*}
       */
      function fixBackendToInputType (meta) {
        let inputType = ''

        meta.required = true

        if (meta.hasOwnProperty('showType')) { // IS_BASIC
          inputType = meta.showType // 只有在基础类型有这个 ...
        } else {
          // FIX SubType
          // subType	number
          // 非必须
          // 题目类型：2单项选择题 3多选 4填空题 5提示条-(跟基础类型5冲突了)
          // TODO: backend fix it !!!
          inputType = parseInt(meta.subType) - 1

          if (inputType === 4) { // @fix 提示条
            inputType = INPUT_TYPES.SHOW_TIP
            meta.required = false
          }
        }

        Object.assign(meta, {
          inputType
        })

        return meta
      }
    } catch (e) {
      handleHttpError(e)
    }
  },

  /**
   * @param code
   * @param page
   * @param query
   * @returns {Promise<*>}
   */
  async getMineSurveyList (code, page = 1, query = {}) {
    try {
      const { data } = await httpClient.post('getMineSurveyList', {
        ...query,
        visualAngle: 1,
        content: {
          cpCode: code,
          startPage: page,
          pageSize: 100
        }
      })
      return data.data.answerLists || []
    } catch (e) {
      handleHttpError(e)
    }
  },

  /**
   * @param data
   * @returns {Promise<void>}
   */
  async saveChatQuestions (data) {
    try {
      const res = await httpClient.post('saveChatQuestions', data)
      return res.data.data
    } catch (e) {
      handleHttpError(e)
    }
  },
  // 趣味测评答题
  async assessuserAnswer (data) {
    try {
      const res = await httpClient.post('/interest/assessuser/v1/answer', data)
      return res.data.data
    } catch (e) {
      handleHttpError(e)
    }
  },
  async userVerification () {
    try {
      const res = await httpClient.get('/assessuser/v1/queryUserInfo')
      return res.data.data
    } catch (e) {
      handleHttpError(e)
    }
  },

  async distributionRelation (data) {
    try {
      const res = await httpClient.post('/assessuser/v1/userShareAccess', data)
      return res.data.data
    } catch (e) {
      handleHttpError(e)
    }
  },
  // 上传图片
  async upFile (file) {
    try {
      const res = await httpClient.post('/interest/assessuser/v1/poster', file)
      return res.data
    } catch (e) {
      handleHttpError(e)
    }
  },
  // 获取分享数据
  async wxShareData (cpCode) {
    try {
      const res = await httpClient.get(`/interest/assessuser/v1/wxShareData?cpCode=${cpCode}`)
      return res.data
    } catch (e) {
      handleHttpError(e)
    }
  },
  // 查询单个测评详情
  async aassessment (cpCode) {
    try {
      const res = await httpClient.get(`/interest/assessuser/v1/assessment/${cpCode}`)
      return res.data
    } catch (e) {
      handleHttpError(e)
    }
  },
  // 获取最后一次问答详情
  async getUserLastedAssess (cpCode, key) {
    let path = `/interest/assessuser/v1/answer/getUserLastedAssess/${cpCode}`
    if (key) {
      path = `${path}?key=${key}`
    }
    try {
      const res = await httpClient.get(path)
      return res.data
    } catch (e) {
      handleHttpError(e)
    }
  },
  // 完成任务
  async accomplishTask (cpCode) {
    try {
      const res = await httpClient.post('/interest/assessuser/v1/accomplishTask', { cpCode })
      return res.data
    } catch (e) {
      handleHttpError(e)
    }
  }
}

const injectMixins = {
  inject: [
    'gState',
    'gActions'
  ],

  data () {
    const isPreview = this.$route.query.hasOwnProperty('preview')
    const cpCode = this.$route.query.code || this.$route.query.id

    return {
      isPreview,
      cpCode,
      code: cpCode,
      pageLoading: false,
      pageErr: null,
      baseInfo: null
    }
  },

  created () {
    this.$watch('pageLoading', (a) => {
      if (a) {
        this.$toast.text('数据载入中...', 'default', {
          time: 10000
        })
      } else {
        this.$toast.hide()
      }
    })
  },

  methods: {
    _ensureCpCode () {
      if (!this.code) {
        this.pageErr = '载入失败'
        this.$toast.error('测评资源有误，请检查 !', 'top', {
          width: '60vw',
          time: 15000
        })
        throw new Error(null)
      }
    },

    async _ensureBaseInfo () {
      let baseInfo = this.gState.tmpSurveyBaseInfo
      if (!baseInfo || !baseInfo.cpCode) {
        baseInfo = await this.gActions.getSurveyBaseInfo(this.code)
        this.gState.tmpSurveyBaseInfo = baseInfo
      }

      this.baseInfo = baseInfo

      // TODO: init wx share
      if (!isVendorXiBao()) {
        if (isWeixin()) {
          setTimeout(async () => {
            const info = await this.gActions.userVerification()
            if (info) {
              baseInfo.accountId = info.accountId
              baseInfo.identity = info.identity
            }
            const isInterest = location.href.includes('interest')
            if (!isInterest) {
              setupWXShare(baseInfo)
            }
          }, 1000)
        }
      }
      // shence
      setupShence()
    }
  }
}

export {
  httpClient,
  httpGWClient,
  actions,
  state,
  injectMixins
}
